import { Pause, Play, Square } from "lucide-react";
import { Button } from "@/v2/components/ui/button";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/v2/components/ui/tooltip";
import { useTimerContext } from "@/v2/contexts/TimerProvider";
import { cn } from "@/v2/lib/utils";

function formatTime(totalSeconds: number): string {
	const h = Math.floor(totalSeconds / 3600);
	const m = Math.floor((totalSeconds % 3600) / 60);
	const s = totalSeconds % 60;
	const mm = String(m).padStart(2, "0");
	const ss = String(s).padStart(2, "0");
	return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/**
 * Inline timer for the Entries toolbar. The v2 entries page has no HeaderBar,
 * so this carries the same timer the other attorney views expose — adapted from
 * the floating `TimerPill` into a top-bar element. Idle shows a Start button;
 * once running it becomes a pill with the elapsed time and transport controls.
 * The square "end" button permanently finishes the timer, collapses it, and
 * opens the Add dialog prefilled with the tracked duration.
 */
export function EntriesTimer({
	onAddEntry,
}: {
	/** Opens the Add-entry dialog prefilled with the tracked duration in seconds. */
	onAddEntry: (durationSeconds: number) => void;
}) {
	const timer = useTimerContext();

	if (timer.phase === "idle") {
		return (
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							variant="outline"
							size="icon"
							onClick={timer.start}
							aria-label="Start timer"
						>
							<Play className="fill-current" />
						</Button>
					</TooltipTrigger>
					<TooltipContent side="bottom">Start timer</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		);
	}

	const isRunning = timer.phase === "running";
	const isPaused = timer.phase === "paused";
	const isStopped = timer.phase === "stopped";
	const displaySeconds = isStopped ? timer.finalSeconds : timer.elapsedSeconds;

	const handleEnd = () => {
		// dismiss() (not finish()) resets the timer to idle without firing onStop,
		// so the shared context's pendingDuration stays null. The tracked duration
		// is handed to the v1 dialog via onAddEntry/local state instead.
		timer.dismiss();
		onAddEntry(displaySeconds);
	};

	return (
		<div className="flex h-10 items-center gap-1.5 rounded-md border border-input bg-background pl-3 pr-1">
			<div className="flex items-center gap-1.5">
				<div
					className={cn(
						"size-2 rounded-full",
						isRunning && "bg-success animate-pulse",
						(isPaused || isStopped) && "bg-warning",
					)}
				/>
				<span className="tabular-nums text-sm font-semibold">
					{formatTime(displaySeconds)}
				</span>
			</div>
			<div className="flex items-center gap-0.5 pl-1">
				{isRunning ? (
					<PillButton onClick={timer.pause} label="Pause">
						<Pause className="size-4" />
					</PillButton>
				) : (
					<PillButton onClick={timer.resume} label="Resume">
						<Play className="size-4 fill-current" />
					</PillButton>
				)}
				<PillButton onClick={handleEnd} label="End and add entry">
					<Square className="size-3.5 fill-current" />
				</PillButton>
			</div>
		</div>
	);
}

function PillButton({
	onClick,
	label,
	children,
}: {
	onClick: () => void;
	label: string;
	children: React.ReactNode;
}) {
	return (
		<TooltipProvider>
			<Tooltip>
				<TooltipTrigger asChild>
					<button
						type="button"
						onClick={onClick}
						aria-label={label}
						className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground active:bg-accent/80"
					>
						{children}
					</button>
				</TooltipTrigger>
				<TooltipContent side="bottom">{label}</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
}
